import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import routes from "routes";
import footerRoutes from "footer.routes";

import bgImage from "assets/images/bg-about-us.jpg";

const activities = [
  { icon: "sports_soccer", title: "Sports", description: "Football, netball, athletics, and other sports activities." },
  { icon: "music_note", title: "Music & Choir", description: "Singing, instrumental music, and choir performances." },
  { icon: "palette", title: "Arts & Crafts", description: "Drawing, painting, and creative crafts projects." },
  { icon: "theater_comedy", title: "Drama", description: "Acting, public speaking, and theatrical performances." },
  { icon: "eco", title: "Environmental Club", description: "Learning about nature and environmental conservation." },
  { icon: "menu_book", title: "Reading Club", description: "Encouraging a love for reading and literature." },
];

const clubs = [
  "Scripture Union",
  "Debate Club",
  "Math Club",
  "Science Club",
  "Art Club",
  "Chess Club",
  "Environmental Club",
  "Music Club",
];

function SchoolLife() {
  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/admissions",
          label: "Enroll Now",
          color: "success",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="75vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              School Life
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.8} mt={1} mb={3}>
              Beyond academics - nurturing the whole child
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* Daily School Life */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6}>
                <MKTypography variant="h3" mb={2}>
                  Daily School Life
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  Every day at Rose of Sharon Primary School is filled with learning, laughter, 
                  and growth. Our daily schedule balances academic instruction with creative 
                  activities, physical exercise, and spiritual development.
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  The school day begins with morning assembly, which includes prayers, hymns, 
                  announcements, and motivational messages. Students then proceed to their 
                  classes for structured learning sessions.
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  Break times allow students to socialize, play, and enjoy nutritious snacks. 
                  Afternoon sessions include continued academic work and co-curricular activities.
                </MKTypography>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox
                  component="img"
                  src="/daily-school-life.jpg"
                  alt="Students at school"
                  borderRadius="lg"
                  shadow="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 400 } }}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Spiritual Development */}
        <MKBox
          component="section"
          py={6}
          sx={{
            background: "linear-gradient(135deg, #006747 0%, #004d35 100%)",
            borderRadius: "xl",
            mx: { xs: 1, lg: 3 },
            my: 4,
          }}
        >
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6}>
                <MKTypography variant="h3" color="white" mb={2}>
                  Spiritual Development
                </MKTypography>
                <MKTypography variant="body1" color="white" opacity={0.9} mb={2}>
                  At Rose of Sharon, spiritual growth is integrated into daily school life. 
                  We believe that nurturing a child&apos;s faith is just as important as developing 
                  their intellect.
                </MKTypography>
                <MKTypography variant="body1" color="white" opacity={0.9} mb={2}>
                  Our spiritual development program includes:
                </MKTypography>
                <MKBox component="ul" sx={{ pl: 2 }}>
                  <MKTypography component="li" variant="body1" color="white" opacity={0.9} mb={1}>
                    Daily morning devotions and prayers
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="white" opacity={0.9} mb={1}>
                    Weekly chapel services with worship and biblical teaching
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="white" opacity={0.9} mb={1}>
                    Scripture Union club activities
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="white" opacity={0.9} mb={1}>
                    Memory verse programs and Bible competitions
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="white" opacity={0.9}>
                    Character education based on biblical principles
                  </MKTypography>
                </MKBox>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox
                  component="img"
                  src="/spiritual-development.jpg"
                  alt="Chapel service"
                  borderRadius="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 350 } }}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Discipline & Behavior */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6} order={{ xs: 2, lg: 1 }}>
                <MKBox
                  component="img"
                  src="/discipline-behavior.jpg"
                  alt="Students in uniform"
                  borderRadius="lg"
                  shadow="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 350 } }}
                />
              </Grid>
              <Grid item xs={12} lg={6} order={{ xs: 1, lg: 2 }}>
                <MKTypography variant="h3" mb={2}>
                  Discipline & Behavior Management
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  We believe that good discipline creates a positive learning environment where 
                  all students can thrive. Our approach to discipline is fair, consistent, and 
                  rooted in respect.
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  Our behavior management system includes:
                </MKTypography>
                <MKBox component="ul" sx={{ pl: 2 }}>
                  <MKTypography component="li" variant="body1" color="text" mb={1}>
                    Clear expectations and school rules
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="text" mb={1}>
                    Positive reinforcement and rewards for good behavior
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="text" mb={1}>
                    Restorative practices that teach responsibility
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="text">
                    Partnership with parents in behavior support
                  </MKTypography>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Co-Curricular Activities */}
        <MKBox component="section" py={6} sx={{ backgroundColor: "#f8f9fa" }}>
          <Container>
            <Grid container justifyContent="center" sx={{ textAlign: "center", mb: 4 }}>
              <Grid item xs={12} md={8}>
                <MKTypography variant="h3" mb={1}>
                  Co-Curricular Activities
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  We offer a variety of activities to develop talents and interests beyond the classroom.
                </MKTypography>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              {activities.map((activity, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <MKBox
                    p={3}
                    textAlign="center"
                    borderRadius="xl"
                    sx={{
                      backgroundColor: "white",
                      boxShadow: ({ boxShadows: { md } }) => md,
                      height: "100%",
                      transition: "transform 0.3s",
                      "&:hover": { transform: "translateY(-5px)" },
                    }}
                  >
                    <MKBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      borderRadius="50%"
                      sx={{ backgroundColor: "#006747", margin: "0 auto 1rem" }}
                    >
                      <Icon sx={{ color: "#d32f2f", fontSize: "1.5rem" }}>{activity.icon}</Icon>
                    </MKBox>
                    <MKTypography variant="h5" mb={1}>
                      {activity.title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {activity.description}
                    </MKTypography>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        {/* Clubs & Talent Development */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6}>
                <MKTypography variant="h3" mb={2}>
                  Clubs & Talent Development
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={3}>
                  Our clubs provide opportunities for students to explore their interests, 
                  develop new skills, and make friends with similar passions. Each club 
                  meets regularly under the guidance of dedicated teachers.
                </MKTypography>
                <MKTypography variant="h6" mb={2}>
                  Available Clubs:
                </MKTypography>
                <Grid container spacing={1}>
                  {clubs.map((club, index) => (
                    <Grid item xs={6} key={index}>
                      <MKBox display="flex" alignItems="center" mb={1}>
                        <Icon sx={{ color: "#006747", mr: 1, fontSize: "1rem" }}>check_circle</Icon>
                        <MKTypography variant="body2">{club}</MKTypography>
                      </MKBox>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox
                  component="img"
                  src="/clubs-talent.jpg"
                  alt="Students in club activities"
                  borderRadius="lg"
                  shadow="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 350 } }}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default SchoolLife;
